<template>
  <b-modal
    :id="modalId"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-backdrop
    @shown="init"
    @hidden="clearForm"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="wizard-form-element">
            <label class="wizard-form-element__label">
              {{ checkboxText }}
            </label>
            <div class="wizard-form-element__control">
              <b-form-checkbox v-model="isNewTemplate"></b-form-checkbox>
            </div>
          </div>

          <div class="wizard-form-element" v-if="isNewTemplate">
            <label
              class="wizard-form-element__label"
              for="import-template-name"
            >
              {{ nameText }}
            </label>
            <div class="wizard-form-element__control">
              <text-input
                id="import-template-name"
                v-model="appName"
                type="text"
                :focus="true"
                @on-text-input-change="onAppNameInputChanged"
              />
            </div>
          </div>

          <div class="wizard-form-element" v-else>
            <label class="wizard-form-element__label">
              {{ selectorText }}
            </label>
            <div class="wizard-form-element__control">
              <search-box-simple
                class="template-list"
                ref="tList"
                :list="templates"
                :value="selectedTemplate"
                @on-search-box-item-selected="onTemplateSelected"
              />
            </div>
          </div>

          <div class="wizard-form-element">
            <label class="wizard-form-element__label" :class="fieldLabelClass">
              {{ fileText }}
            </label>
            <div class="wizard-form-element__control">
              <file-upload @on-file-change="onFileUpload"></file-upload>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="clearForm">
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="modal-btn modal-save-btn"
        @click="doImport"
        :disabled="shouldBeDisabled"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import FileUpload from "@/components/FileUpload";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import TextInput from "@/components/TextInput";
  import templateService from "@/services/templates-service";
  import store from "@/store";
  import { MUTATION_NAME, STATUS_CODE } from "@/static/global-variables";

  export default {
    name: "ImportTemplateModal",
    components: {
      SearchBoxSimple,
      TextInput,
      FileUpload
    },
    props: {
      appId: {
        default: "",
        type: String
      },
      isUpdateTemplate: {
        default: false,
        type: Boolean
      },
      template: {
        default: null,
        type: Object
      }
    },
    data() {
      return {
        appName: "",
        isNewTemplate: false,
        selectedTemplate: null,
        uploadedFile: null
      };
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages"
      }),
      modalId() {
        return "import-template-" + this.appId;
      },
      fieldLabelClass() {
        return this.uploadedFile ? "field-label-low" : "field-label-high";
      },
      templates() {
        return this.apps.filter(a => a.isTemplate);
      },
      titleText() {
        return this.$t("newPageTemplateFromFile");
      },
      checkboxText() {
        return this.$t("createNewPageTemplate");
      },
      nameText() {
        return this.$t("pageTemplateName");
      },
      selectorText() {
        return this.$t("pageTemplates");
      },

      fileText() {
        return this.$t("file");
      },
      shouldBeDisabled() {
        let conditions = [!this.uploadedFile];
        let last = this.isNewTemplate ? !this.appName : !this.selectedTemplate;

        conditions.push(last);
        return conditions.some(c => c);
      }
    },
    methods: {
      init() {
        this.isNewTemplate = !this.isUpdateTemplate;
        this.onTemplateSelected(this.template);
      },

      clearForm() {
        this.appName = "";
        this.isNewTemplate = false;
        this.selectedTemplate = null;
        this.uploadedFile = null;
        this.$bvModal.hide(this.modalId);
      },
      onTemplateSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.selectedTemplate = null;
        } else {
          this.selectedTemplate = ctx;
        }
      },
      onFileUpload(val) {
        this.uploadedFile = val.file;
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, {
          solid: true,
          toaster: "b-toaster-bottom-center",
          variant: "danger"
        });
      },
      errorHandler(error) {
        const component = this;
        const invalidFormatMsg = component.$t("invalidPageTemplateFileFormat");
        const saveError = component.getMessageText(
          component.messages.SAVE_ERROR
        );
        if (
          error &&
          error.response &&
          error.response.status === STATUS_CODE.BAD_REQUEST
        ) {
          this.displayErrorMessage(invalidFormatMsg);
        } else {
          this.displayErrorMessage(saveError);
        }
      },
      navigateToEditPage(app) {
        this.$router.push({
          name: "DataPalette",
          params: {
            appId: app.uuid,
            appName: app.name,
            type: app.type,
            testRecord: app.testRecord
          }
        });
      },
      doImport() {
        if (this.isNewTemplate) {
          templateService
            .createWithFile(this.appName, null, this.uploadedFile, true)
            .then(resp => {
              if (!resp) {
                this.errorHandler();
                return;
              }

              resp.dataElements = {};
              resp.pages = [];
              resp.isDeployable = true;

              store.commit(MUTATION_NAME.ADD_APP, resp);
              this.navigateToEditPage(resp);
            }, this.errorHandler)
            .catch(this.errorHandler);
        } else {
          const selected = this.selectedTemplate;
          templateService
            .updateWithFile(this.selectedTemplate.uuid, this.uploadedFile)
            .then(resp => {
              if (!resp) {
                this.errorHandler();
                return;
              }

              const app = JSON.parse(JSON.stringify(selected));
              app.version = resp.version;
              store.commit(MUTATION_NAME.UPDATE_APP, app);
              this.navigateToEditPage(resp);
            }, this.errorHandler)
            .catch(this.errorHandler);
        }
        this.clearForm();
      },

      onAppNameInputChanged(val) {
        this.appName = val;
      }
    }
  };
</script>

<style scoped>
  .wizard-form-element {
    display: block;
    padding: 0.25rem;
    width: 100%;
    flex-basis: 100%;
    margin-bottom: 1rem;
    position: relative;
    min-width: 0;
  }

  @media (min-width: 48em) {
    .wizard-form-element__label {
      float: left;
      max-width: calc(33% - 1.25rem);
      flex-basis: calc(33% - 1.25rem);
      margin-bottom: 0;
      position: relative;
      z-index: 1;
    }

    .wizard-form-element__control {
      padding-left: 33%;
      clear: none;
    }
  }

  .wizard-form-element__label {
    float: left;
    overflow-wrap: break-word;
    word-wrap: break-word;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;
    display: inline-block;
    color: var(--LIST_RESULTS, #3e3e3c);
    font-size: 0.75rem;
    padding-right: 0.5rem;
    padding-top: 0.25rem;
    margin-bottom: 0.125rem;
  }

  .wizard-form-element__control {
    position: relative;
  }

  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: var(--BTN_BG_BLUE, #0070d2);
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: var(--BTN_BG_BLUE, #0070d2);
    border: 1px solid var(--BTN_BG_BLUE, #0070d2);
    color: var(--WHITE, #fff);
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid var(--LIGHT_GRAY, #dddbda);
    background-color: var(--WHITE, #fff);
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE, #fff);
  }
</style>
